<template>
    <div class="login-page">

        <Head title="Вход" />

        <div class="login-container">
            <div class="login-card">

                <div class="login-header">
                    <h1>Вход в систему</h1>
                    <p>Войдите для доступа к личному кабинету</p>
                    <div class="telegram-info">
                        <p class="telegram-text">
                            Чтобы получить <strong>логин</strong> и <strong>пароль</strong>, напишите свою
                            фамилию и имя в личные сообщения в нашем Telegram:
                            <a href="https://t.me/volleyball_by" target="_blank" rel="noopener noreferrer"
                                class="telegram-link">
                                <svg class="telegram-icon" width="16" height="16" viewBox="0 0 24 24"
                                    fill="currentColor">
                                    <path
                                        d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
                                </svg>
                                @volleyball_by
                            </a>
                        </p>
                    </div>
                </div>

                <form @submit.prevent="submit" class="login-form">
                    <div class="form-group">
                        <label for="username" class="form-label">Логин</label>
                        <UiInput id="username" v-model="form.username" type="text" label="" block required autofocus
                            autocomplete="username" placeholder="Введите логин" :error="form.errors.username" />
                    </div>

                    <div class="form-group">
                        <label for="password" class="form-label">Пароль</label>
                        <UiInput id="password" v-model="form.password" type="password" label="" block required
                            autocomplete="current-password" placeholder="Введите пароль" :error="form.errors.password" />
                    </div>

                    <div class="form-checkbox">
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="form.remember" />
                            <span>Запомнить меня</span>
                        </label>
                    </div>

                    <button type="submit" class="btn-submit" :disabled="form.processing">
                        <span v-if="form.processing">Вход...</span>
                        <span v-else>Войти</span>
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import UiInput from '@/components/ui/forms/UiInput.vue';

const form = useForm({
    username: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform((data) => ({
        ...data,
        username: data.username.trim(),
        password: data.password.trim(),
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.login-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background:
        radial-gradient(circle at top left, rgba(58, 106, 184, 0.25), transparent 42%),
        radial-gradient(circle at bottom right, $color-brand-alpha, transparent 45%),
        linear-gradient(135deg, $color-bg-light 0%, #e6eef8 100%);
    padding: $space-4;
}

.login-container {
    width: 100%;
    max-width: 420px;
}

.login-card {
    background: $color-bg-primary;
    border: 1px solid $color-brand-alpha;
    border-radius: $radius-lg;
    padding: $space-6 $space-5;
    box-shadow: $shadow-xl;

    @include up($bp-sm) {
        padding: $space-6 $space-8;
    }
}

.login-header {
    text-align: center;
    margin-bottom: 32px;

    h1 {
        font-size: 20px;
        font-weight: 700;
        color: $color-text-dark;
        margin-bottom: 8px;
    }

    p {
        font-size: 14px;
        color: $color-text-secondary;
    }
}

.telegram-info {
    margin-top: 16px;
    padding: 12px 16px;
    background: linear-gradient(135deg, $color-brand-alpha-soft 0%, $color-brand-alpha 100%);
    border-radius: $radius-sm;
    border: 1px solid $color-brand-border;

    .telegram-text {
        font-size: 13px;
        color: $color-text-primary;
        margin: 0;
        line-height: 1.5;

        strong {
            font-weight: 600;
            color: $color-text-dark;
        }
    }

    .telegram-link {
        display: inline;
        color: #0088cc;
        text-decoration: none;
        transition: all 0.2s;
        margin-left: 4px;
        vertical-align: baseline;

        &:hover {
            color: #0066aa;
            text-decoration: underline;
        }

        .telegram-icon {
            width: 16px;
            height: 16px;
            display: inline-block;
            vertical-align: middle;
            margin-right: 2px;
        }
    }
}

.login-form {
    display: flex;
    flex-direction: column;

    .form-group {
        margin-bottom: 20px;
    }

    .form-label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: $color-text-primary;
        margin-bottom: 8px;
    }

    .form-input {
        width: 100%;
        padding: 12px 16px;
        font-size: 15px;
        border: 2px solid $color-border-light;
        border-radius: 8px;
        transition: all 0.2s;
        background: $color-bg-secondary;

        &:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            background: $color-bg-primary;
        }

        &::placeholder {
            color: $color-text-light;
        }

        &-error {
            border-color: $color-error;
            background: $color-error-bg;
        }
    }

    .form-error {
        margin-top: 6px;
        font-size: 13px;
        color: $color-error;
    }

    .form-checkbox {
        width: 100%;
        margin-bottom: 24px;

        .checkbox-label {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            user-select: none;

            input[type="checkbox"] {
                width: 18px;
                height: 18px;
                cursor: pointer;
                accent-color: $color-brand;
            }

            span {
                font-size: 14px;
                color: $color-text-primary;
            }
        }
    }

    .btn-submit {
        width: 100%;
        min-height: $touch-min;
        padding: $space-3;
        font-size: 16px;
        font-weight: 600;
        color: $color-bg-primary;
        background: $color-brand;
        border: none;
        border-radius: $radius-sm;
        cursor: pointer;
        transition: all 0.3s;
        box-shadow: 0 4px 12px rgba(39, 80, 146, 0.3);

        &:hover:not(:disabled) {
            background: $color-brand-dark;
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(39, 80, 146, 0.4);
        }

        &:active:not(:disabled) {
            transform: translateY(0);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}
</style>
